import { useEffect, useState } from "react";
import api, { formatApiError, formatDate, formatIDR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { RiskBadge, StatusBadge, REC_LABELS } from "./badges";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { CircleNotch, FilePdf, ArrowsClockwise, Warning } from "@phosphor-icons/react";
import DocumentsSection from "./claim/DocumentsSection";
import ReportSection from "./claim/ReportSection";
import AiAnalysisSection from "./claim/AiAnalysisSection";
import DocAnalysisSection from "./claim/DocAnalysisSection";
import ExternalHandoverDocs from "./claim/ExternalHandoverDocs";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Info = ({ label, value, testId }) => (
  <div className="border border-border/60 p-3">
    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
    <p className="text-sm font-medium" data-testid={testId}>{value ?? "—"}</p>
  </div>
);

const CAUSE_LABELS = { sakit: "Sakit", kecelakaan: "Kecelakaan", tidak_diketahui: "Tidak Diketahui" };

const ScoreBreakdown = ({ breakdown }) => (
  <div>
    <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">Rincian Skor</h3>
    <div className="border border-border">
      {breakdown.length === 0 && <p className="p-3 text-sm text-muted-foreground">Tidak ada aturan yang terpicu — indikasi risiko rendah.</p>}
      {breakdown.map((b, i) => (
        <div key={`${b.rule_name}-${i}`} data-testid={`score-breakdown-row-${i}`} className="flex items-center justify-between px-3 py-2.5 border-b border-border/60 last:border-0">
          <div>
            <p className="text-sm font-medium">{b.rule_name}</p>
            <p className="text-xs text-muted-foreground">{b.description}</p>
          </div>
          <span className="font-mono text-sm font-semibold text-red-700">+{b.points}</span>
        </div>
      ))}
    </div>
  </div>
);

const PhraseHits = ({ hits }) => {
  if (!hits?.length) return null;
  return (
    <div>
      <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Frasa Mencurigakan Terdeteksi</h3>
      <div className="flex flex-wrap gap-2">
        {hits.map((h, i) => (
          <span key={h.phrase} data-testid={`phrase-hit-${i}`} className="text-xs border border-amber-300 bg-amber-50 text-amber-800 px-2 py-1">
            "{h.phrase}" <span className="font-mono font-semibold">+{h.weight}</span>
          </span>
        ))}
      </div>
    </div>
  );
};

export default function ClaimDetailSheet({ claimId, onClose, onChanged }) {
  const { user } = useAuth();
  const [claim, setClaim] = useState(null);
  const [investigators, setInvestigators] = useState([]);
  const [selectedInv, setSelectedInv] = useState("");
  const [invType, setInvType] = useState("internal");
  const [note, setNote] = useState("");
  const [acting, setActing] = useState(false);
  const [handoverReady, setHandoverReady] = useState(false);
  const [docsRefreshKey, setDocsRefreshKey] = useState(0);

  const canDecide = ["admin", "supervisor"].includes(user.role);
  const canRunAi = ["admin", "supervisor", "staff"].includes(user.role);
  const needApproval = claim?.status === "menunggu_approval";
  const needAssign = claim?.status === "menunggu_penugasan";
  const isAssignee = claim?.assigned_to === user.id && claim?.status === "ditugaskan";
  const canUploadDocs = ["admin", "supervisor", "staff"].includes(user.role) || claim?.assigned_to === user.id;
  const needsHandover = needApproval || (needAssign && invType === "eksternal");

  const refreshClaim = () => api.get(`/claims/${claimId}`).then((r) => setClaim(r.data)).catch(() => {});

  useEffect(() => {
    if (!claimId) { setClaim(null); return; }
    setNote(""); setSelectedInv(""); setInvType("internal"); setHandoverReady(false);
    api.get(`/claims/${claimId}`).then((r) => setClaim(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [claimId]);

  useEffect(() => {
    if (!claim || !canDecide) return;
    const role = needApproval ? "investigator_eksternal"
      : needAssign ? (invType === "eksternal" ? "investigator_eksternal" : "investigator_internal") : null;
    if (!role) return;
    api.get("/users", { params: { role } }).then((r) => {
      setInvestigators(r.data);
      if (role === "investigator_eksternal") {
        const nayaka = r.data.find((u) => u.email === "nayaka.integritas@gmail.com");
        setSelectedInv(nayaka?.id || r.data[0]?.id || "");
      } else {
        setSelectedInv("");
      }
    }).catch(() => {});
  }, [claim, canDecide, needApproval, needAssign, invType]);

  const act = async (path, body) => {
    setActing(true);
    try {
      const { data } = await api.post(`/claims/${claimId}/${path}`, body);
      toast.success(data.message);
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing(false);
    }
  };

  const rescore = async () => {
    setActing(true);
    try {
      const { data } = await api.post(`/claims/${claimId}/rescore`);
      toast.success(`Skor dihitung ulang: ${data.score}/100 (${data.risk_category})`);
      setClaim(data);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing(false);
    }
  };

  return (
    <Sheet open={!!claimId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="claim-detail-sheet">
        {!claim && <SheetHeader className="px-6 pt-6"><SheetTitle className="sr-only">Detail Klaim</SheetTitle></SheetHeader>}
        {claim && (
          <>
            <SheetHeader className="px-6 pt-6 pb-4 border-b border-border">
              <div className="flex items-center justify-between mr-6">
                <div>
                  <SheetTitle className="font-mono text-base" data-testid="detail-claim-number">{claim.claim_number}</SheetTitle>
                  <p className="text-xs text-muted-foreground mt-1 capitalize">Asuransi {claim.claim_type} · Dibuat {formatDate(claim.created_at)} oleh {claim.created_by_name}</p>
                </div>
                <div className="flex items-center gap-2">
                  {["admin", "supervisor"].includes(user.role) && (
                    <>
                      <button data-testid="rescore-button" onClick={rescore} disabled={acting} title="Hitung ulang skor dengan kriteria terbaru"
                        className="inline-flex items-center gap-1.5 border border-border px-2.5 py-1 text-xs font-medium hover:border-slate-900 hover:text-slate-900 transition-colors duration-150 disabled:opacity-50">
                        {acting ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />} Rescore
                      </button>
                      <a data-testid="export-pdf-button" href={`${BACKEND}/api/claims/${claim.id}/export-pdf`}
                        className="inline-flex items-center gap-1.5 border border-border px-2.5 py-1 text-xs font-medium hover:border-slate-900 hover:text-slate-900 transition-colors duration-150">
                        <FilePdf size={14} /> PDF
                      </a>
                    </>
                  )}
                  <RiskBadge category={claim.risk_category} />
                  <StatusBadge status={claim.status} />
                </div>
              </div>
            </SheetHeader>

            <div className="px-6 py-5 space-y-6">
              <div className="flex items-center gap-6 bg-slate-900 text-white p-5">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">Skor Fraud</p>
                  <p className="text-4xl font-bold font-mono" data-testid="detail-score">{claim.score}<span className="text-lg text-slate-400">/100</span></p>
                </div>
                <div className="border-l border-white/20 pl-6">
                  <p className="text-[10px] uppercase tracking-wider text-slate-400">Rekomendasi</p>
                  <p className="text-sm font-semibold mt-1" data-testid="detail-recommendation">{REC_LABELS[claim.recommendation]}</p>
                </div>
              </div>

              {claim.claim_type === "kendaraan" && claim.derived?.name_mismatch && (
                <div data-testid="name-mismatch-alert" className="border border-red-300 bg-red-50 p-3 flex gap-2.5 text-sm text-red-800">
                  <Warning size={18} className="shrink-0 mt-0.5" />
                  <p><b>Indikasi pinjam nama / oper alih:</b> nama pelapor ({claim.reporter_name}) berbeda dengan tertanggung ({claim.insured_name}).
                    Potensi pelanggaran <b>Pasal 27 ayat 4 PSAKBI</b> — pertanggungan batal demi hukum apabila tidak ada hubungan kepemilikan antara kendaraan dengan Tertanggung.</p>
                </div>
              )}

              {claim.claim_type === "kendaraan" && claim.derived?.holder_mismatch && (
                <div data-testid="holder-mismatch-alert" className="border border-amber-300 bg-amber-50 p-3 flex gap-2.5 text-sm text-amber-800">
                  <Warning size={18} className="shrink-0 mt-0.5" />
                  <p><b>Cek silang master polis:</b> pelapor ({claim.reporter_name}) berbeda dengan pemegang polis terdaftar ({claim.policy_holder_name}).
                    Verifikasi hubungan kepemilikan — indikasi pinjam nama / oper alih (Pasal 27 ayat 4 PSAKBI).</p>
                </div>
              )}

              {claim.claim_type === "kendaraan" && claim.derived?.commercial_use_mismatch && (
                <div data-testid="commercial-use-alert" className="border border-red-300 bg-red-50 p-3 flex gap-2.5 text-sm text-red-800">
                  <Warning size={18} className="shrink-0 mt-0.5" />
                  <p><b>Indikasi penggunaan komersial:</b> kronologi mengindikasikan kendaraan disewakan/digunakan komersial
                    {claim.derived?.commercial_keywords?.length ? <> (kata kunci: <b>{claim.derived.commercial_keywords.join(", ")}</b>)</> : null} padahal polis tercatat penggunaan Pribadi/Dinas.
                    Potensi pelanggaran <b>Pasal 8 PSAKBI</b> — perubahan penggunaan kendaraan wajib diberitahukan maksimal 7 hari kalender; tanpa pemberitahuan, klaim berpotensi gugur.</p>
                </div>
              )}

              <div>
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">Informasi Klaim</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <Info label="No. Polis" value={claim.policy_number} />
                  <Info label="Nilai Klaim" value={formatIDR(claim.claim_amount)} />
                  <Info label="Pelapor" value={claim.reporter_name} />
                  <Info label="Mulai Polis" value={formatDate(claim.policy_start_date)} />
                  <Info label="Tgl Kejadian" value={formatDate(claim.incident_date)} />
                  <Info label="Lokasi" value={`${claim.district ? claim.district + ", " : ""}${claim.city ? claim.city + ", " : ""}${claim.province}`} />
                  {claim.claim_type === "jiwa" ? (
                    <>
                      <Info label="Tertanggung" value={claim.insured_name} />
                      <Info label="Usia" value={claim.insured_age ? `${claim.insured_age} thn` : null} />
                      <Info label="Penyebab" value={CAUSE_LABELS[claim.cause_of_death] || claim.cause_of_death} />
                    </>
                  ) : (
                    <>
                      <Info label="Tertanggung" value={claim.insured_name} testId="detail-kendaraan-insured" />
                      {claim.policy_holder_name && <Info label="Pemegang Polis (Master)" value={claim.policy_holder_name} testId="detail-policy-holder" />}
                      <Info label="Kendaraan" value={claim.vehicle_brand} />
                      <Info label="No. Polisi" value={claim.plate_number} />
                      <Info label="Nilai Kendaraan" value={claim.vehicle_value ? formatIDR(claim.vehicle_value) : null} />
                      <Info label="Jenis Asuransi" value={claim.coverage_type === "all_risk" ? "All Risk" : claim.coverage_type === "tlo" ? "TLO" : null} />
                      <Info label="Jenis Penggunaan" value={claim.usage_type === "pribadi_dinas" ? "Pribadi / Dinas" : claim.usage_type === "komersial_disewakan" ? "Komersial / Disewakan" : null} testId="detail-usage-type" />
                      {claim.policy_end_date && <Info label="Berakhir Polis" value={formatDate(claim.policy_end_date)} />}
                      <Info label="Laporan Polisi" value={claim.police_report ? "Ada" : "Tidak ada"} />
                      <Info label="Saksi" value={claim.witness_present ? "Ada" : "Tidak ada"} />
                      <Info label="Jam Kejadian" value={claim.incident_hour != null ? `${String(claim.incident_hour).padStart(2, "0")}.00` : null} />
                    </>
                  )}
                  <Info label="Dokumen" value={claim.documents_complete ? "Lengkap" : `Tidak lengkap${claim.missing_documents?.length ? ` — kurang: ${claim.missing_documents.join(", ")}` : ""}`} />
                  <Info label="Klaim Sebelumnya" value={claim.previous_claims_count} />
                  {claim.assigned_to_name && <Info label="Investigator" value={claim.assigned_to_name} testId="detail-investigator" />}
                </div>
              </div>

              <div>
                <h3 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">Kronologi</h3>
                <p className="text-sm border border-border/60 p-3 leading-relaxed whitespace-pre-wrap">{claim.chronology}</p>
              </div>

              <ScoreBreakdown breakdown={claim.score_breakdown} />

              <PhraseHits hits={claim.phrase_hits} />

              <AiAnalysisSection claim={claim} canRun={canRunAi}
                onChanged={() => { refreshClaim(); onChanged?.(); }} />

              <DocumentsSection key={docsRefreshKey} claim={claim} canUpload={canUploadDocs} />

              <DocAnalysisSection claim={claim} canRun={canRunAi}
                onChanged={() => { refreshClaim(); onChanged?.(); }} />

              <ReportSection claim={claim} canSubmit={isAssignee}
                onDone={() => { onChanged?.(); onClose(); }} />

              {(claim.approval_note || (claim.completion_note && !claim.investigation_report)) && (
                <div className="space-y-2">
                  {claim.approval_note && <Info label={claim.rejected_by ? `Catatan Penolakan (${claim.rejected_by})` : `Catatan Approval (${claim.approved_by || ""})`} value={claim.approval_note} />}
                  {claim.completion_note && !claim.investigation_report && <Info label="Catatan Hasil Investigasi" value={claim.completion_note} />}
                </div>
              )}

              {canDecide && (needApproval || needAssign) && (
                <div className="border border-slate-900 p-4 space-y-3">
                  <h3 className="text-sm font-semibold">
                    {needApproval ? "Approval Investigator Eksternal" : "Penugasan Investigator"}
                  </h3>
                  {needAssign && (
                    <div className="space-y-1.5">
                      <p className="text-xs text-muted-foreground">Rekomendasi sistem: investigator internal. Anda dapat mengalihkan ke investigator eksternal bila diperlukan.</p>
                      <Select value={invType} onValueChange={setInvType}>
                        <SelectTrigger data-testid="select-investigator-type"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="internal">Investigator Internal (rekomendasi sistem)</SelectItem>
                          <SelectItem value="eksternal">Alihkan ke Investigator Eksternal</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <Select value={selectedInv} onValueChange={setSelectedInv}>
                    <SelectTrigger data-testid="select-investigator"><SelectValue placeholder="Pilih investigator" /></SelectTrigger>
                    <SelectContent>
                      {investigators.map((inv) => <SelectItem key={inv.id} value={inv.id}>{inv.name} — {inv.email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {needsHandover && (
                    <ExternalHandoverDocs claimId={claim.id}
                      onReadyChange={setHandoverReady}
                      onUploaded={() => setDocsRefreshKey((k) => k + 1)} />
                  )}
                  <Textarea data-testid="action-note-input" rows={2} placeholder="Catatan (opsional)" value={note} onChange={(e) => setNote(e.target.value)} />
                  <div className="flex gap-2">
                    <Button data-testid="approve-assign-button" disabled={!selectedInv || acting || (needsHandover && !handoverReady)}
                      onClick={() => act(needApproval ? "approve" : "assign", { investigator_id: selectedInv, note })}
                      className="bg-slate-900 hover:bg-slate-800 rounded-none flex-1">
                      {acting ? <CircleNotch size={16} className="animate-spin" /> : needApproval ? "Setujui & Tugaskan" : "Tugaskan"}
                    </Button>
                    {needApproval && (
                      <Button data-testid="reject-button" variant="outline" disabled={acting}
                        onClick={() => act("reject", { note })}
                        className="rounded-none border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800">
                        Tolak (alihkan internal)
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
